/**
 * Generated by orval v8.2.0 🍺
 * Do not edit manually.
 * Fragment Payments API
 * REST API for Fragment payment operations
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
  BatchGetInvoicesRequest,
  BatchGetInvoicesResponse,
  CreateExternalAccountRequest,
  CreateInvoiceRequest,
  CreatePaymentFlowRequest,
  CreateProductRequest,
  CreateUserRequest,
  ErrorResponse,
  ExternalAccountListResponse,
  ExternalAccountSuccessResponse,
  GetTransactionsParams,
  InvoiceDetailSuccessResponse,
  InvoiceHistoryResponse,
  InvoiceListResponse,
  InvoiceSuccessResponse,
  PaymentFlowListRequest,
  PaymentFlowListResponse,
  PaymentFlowSuccessResponse,
  PaymentListRequest,
  PaymentListResponse,
  PaymentSuccessResponse,
  ProductListResponse,
  ProductSuccessResponse,
  SearchAllocationsRequest,
  SearchAllocationsResponse,
  SearchInvoicesRequest,
  SearchInvoicesResponse,
  SearchTransactionsRequest,
  SearchTransactionsResponse,
  SyncTransactionRequest,
  TransactionHistoryResponse,
  TransactionListResponse,
  TransactionSuccessResponse,
  UpdateInvoiceRequest,
  UpdateTransactionRequest,
  UpdateUserRequest,
  UserListResponse,
  UserSuccessResponse,
} from '../../../payments-api/model';

import { usePaymentsApiHttpClient } from '../../../wrappers/PaymentsApiAuthProvider';

/**
 * Creates an external account.
 */
export const usePostExternalAccountsHook = () => {
  const postExternalAccounts = usePaymentsApiHttpClient<ExternalAccountSuccessResponse>();

  return useCallback(
    (createExternalAccountRequest: CreateExternalAccountRequest, signal?: AbortSignal) => {
      return postExternalAccounts({
        url: `/external-accounts`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createExternalAccountRequest,
        signal,
      });
    },
    [postExternalAccounts],
  );
};

export const usePostExternalAccountsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
    TError,
    { data: CreateExternalAccountRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
  TError,
  { data: CreateExternalAccountRequest },
  TContext
> => {
  const mutationKey = ['postExternalAccounts'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postExternalAccounts = usePostExternalAccountsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
    { data: CreateExternalAccountRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postExternalAccounts(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostExternalAccountsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>
>;
export type PostExternalAccountsMutationBody = CreateExternalAccountRequest;
export type PostExternalAccountsMutationError = ErrorResponse;

export const usePostExternalAccounts = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
      TError,
      { data: CreateExternalAccountRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostExternalAccountsHook>>>,
  TError,
  { data: CreateExternalAccountRequest },
  TContext
> => {
  return useMutation(usePostExternalAccountsMutationOptions(options), queryClient);
};

/**
 * Lists all external accounts.
 */
export const useGetExternalAccountsHook = () => {
  const getExternalAccounts = usePaymentsApiHttpClient<ExternalAccountListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getExternalAccounts({
        url: `/external-accounts`,
        method: 'GET',
        signal,
      });
    },
    [getExternalAccounts],
  );
};

export const getGetExternalAccountsQueryKey = () => {
  return [`/external-accounts`] as const;
};

export const useGetExternalAccountsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExternalAccountsQueryKey();

  const getExternalAccounts = useGetExternalAccountsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>> = ({ signal }) =>
    getExternalAccounts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetExternalAccountsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
>;
export type GetExternalAccountsQueryError = ErrorResponse;

export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetExternalAccounts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetExternalAccountsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetExternalAccountsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<
    UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetExternalAccountsQueryKey();

  const getExternalAccounts = useGetExternalAccountsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>> = ({ signal }) =>
    getExternalAccounts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetExternalAccountsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>
>;
export type GetExternalAccountsSuspenseQueryError = ErrorResponse;

export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetExternalAccountsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetExternalAccountsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetExternalAccountsSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates an invoice.
 */
export const usePostInvoicesHook = () => {
  const postInvoices = usePaymentsApiHttpClient<InvoiceSuccessResponse>();

  return useCallback(
    (createInvoiceRequest: CreateInvoiceRequest, signal?: AbortSignal) => {
      return postInvoices({
        url: `/invoices`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createInvoiceRequest,
        signal,
      });
    },
    [postInvoices],
  );
};

export const usePostInvoicesMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
    TError,
    { data: CreateInvoiceRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
  TError,
  { data: CreateInvoiceRequest },
  TContext
> => {
  const mutationKey = ['postInvoices'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postInvoices = usePostInvoicesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
    { data: CreateInvoiceRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postInvoices(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostInvoicesMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>>;
export type PostInvoicesMutationBody = CreateInvoiceRequest;
export type PostInvoicesMutationError = ErrorResponse;

export const usePostInvoices = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
      TError,
      { data: CreateInvoiceRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesHook>>>,
  TError,
  { data: CreateInvoiceRequest },
  TContext
> => {
  return useMutation(usePostInvoicesMutationOptions(options), queryClient);
};

/**
 * Lists all invoices.
 */
export const useGetInvoicesHook = () => {
  const getInvoices = usePaymentsApiHttpClient<InvoiceListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getInvoices({ url: `/invoices`, method: 'GET', signal });
    },
    [getInvoices],
  );
};

export const getGetInvoicesQueryKey = () => {
  return [`/invoices`] as const;
};

export const useGetInvoicesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesQueryKey();

  const getInvoices = useGetInvoicesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>> = ({ signal }) =>
    getInvoices(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>>;
export type GetInvoicesQueryError = ErrorResponse;

export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoices<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesQueryKey();

  const getInvoices = useGetInvoicesHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>> = ({ signal }) =>
    getInvoices(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>>;
export type GetInvoicesSuspenseQueryError = ErrorResponse;

export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Retrieves an invoice.
 */
export const useGetInvoicesIdHook = () => {
  const getInvoicesId = usePaymentsApiHttpClient<InvoiceDetailSuccessResponse>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getInvoicesId({ url: `/invoices/${id}`, method: 'GET', signal });
    },
    [getInvoicesId],
  );
};

export const getGetInvoicesIdQueryKey = (id: string) => {
  return [`/invoices/${id}`] as const;
};

export const useGetInvoicesIdQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdQueryKey(id);

  const getInvoicesId = useGetInvoicesIdHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>> = ({ signal }) =>
    getInvoicesId(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInvoicesIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>>;
export type GetInvoicesIdQueryError = ErrorResponse;

export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesId<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesIdSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdQueryKey(id);

  const getInvoicesId = useGetInvoicesIdHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>> = ({ signal }) =>
    getInvoicesId(id, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesIdSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>
>;
export type GetInvoicesIdSuspenseQueryError = ErrorResponse;

export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdSuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an invoice.
 */
export const usePatchInvoicesIdHook = () => {
  const patchInvoicesId = usePaymentsApiHttpClient<InvoiceSuccessResponse>();

  return useCallback(
    (id: string, updateInvoiceRequest: UpdateInvoiceRequest, signal?: AbortSignal) => {
      return patchInvoicesId({
        url: `/invoices/${id}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateInvoiceRequest,
        signal,
      });
    },
    [patchInvoicesId],
  );
};

export const usePatchInvoicesIdMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>,
    TError,
    { id: string; data: UpdateInvoiceRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>,
  TError,
  { id: string; data: UpdateInvoiceRequest },
  TContext
> => {
  const mutationKey = ['patchInvoicesId'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const patchInvoicesId = usePatchInvoicesIdHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>,
    { id: string; data: UpdateInvoiceRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return patchInvoicesId(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchInvoicesIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>>;
export type PatchInvoicesIdMutationBody = UpdateInvoiceRequest;
export type PatchInvoicesIdMutationError = ErrorResponse;

export const usePatchInvoicesId = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>,
      TError,
      { id: string; data: UpdateInvoiceRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePatchInvoicesIdHook>>>,
  TError,
  { id: string; data: UpdateInvoiceRequest },
  TContext
> => {
  return useMutation(usePatchInvoicesIdMutationOptions(options), queryClient);
};

/**
 * Retrieves the version history of an invoice.
 */
export const useGetInvoicesIdHistoryHook = () => {
  const getInvoicesIdHistory = usePaymentsApiHttpClient<InvoiceHistoryResponse>();

  return useCallback(
    (id: string, signal?: AbortSignal) => {
      return getInvoicesIdHistory({
        url: `/invoices/${id}/history`,
        method: 'GET',
        signal,
      });
    },
    [getInvoicesIdHistory],
  );
};

export const getGetInvoicesIdHistoryQueryKey = (id: string) => {
  return [`/invoices/${id}/history`] as const;
};

export const useGetInvoicesIdHistoryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdHistoryQueryKey(id);

  const getInvoicesIdHistory = useGetInvoicesIdHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>> = ({ signal }) =>
    getInvoicesIdHistory(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInvoicesIdHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
>;
export type GetInvoicesIdHistoryQueryError = ErrorResponse;

export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdHistoryQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetInvoicesIdHistorySuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoicesIdHistoryQueryKey(id);

  const getInvoicesIdHistory = useGetInvoicesIdHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>> = ({ signal }) =>
    getInvoicesIdHistory(id, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetInvoicesIdHistorySuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>
>;
export type GetInvoicesIdHistorySuspenseQueryError = ErrorResponse;

export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetInvoicesIdHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>,
  TError = ErrorResponse,
>(
  id: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetInvoicesIdHistoryHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetInvoicesIdHistorySuspenseQueryOptions(id, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Searches invoices.
 */
export const usePostInvoicesSearchHook = () => {
  const postInvoicesSearch = usePaymentsApiHttpClient<SearchInvoicesResponse>();

  return useCallback(
    (searchInvoicesRequest: SearchInvoicesRequest, signal?: AbortSignal) => {
      return postInvoicesSearch({
        url: `/invoices/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchInvoicesRequest,
        signal,
      });
    },
    [postInvoicesSearch],
  );
};

export const usePostInvoicesSearchMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>,
    TError,
    { data: SearchInvoicesRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>,
  TError,
  { data: SearchInvoicesRequest },
  TContext
> => {
  const mutationKey = ['postInvoicesSearch'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postInvoicesSearch = usePostInvoicesSearchHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>,
    { data: SearchInvoicesRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postInvoicesSearch(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostInvoicesSearchMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>
>;
export type PostInvoicesSearchMutationBody = SearchInvoicesRequest;
export type PostInvoicesSearchMutationError = ErrorResponse;

export const usePostInvoicesSearch = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>,
      TError,
      { data: SearchInvoicesRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesSearchHook>>>,
  TError,
  { data: SearchInvoicesRequest },
  TContext
> => {
  return useMutation(usePostInvoicesSearchMutationOptions(options), queryClient);
};

/**
 * Retrieves multiple invoices.
 */
export const usePostInvoicesBatchGetHook = () => {
  const postInvoicesBatchGet = usePaymentsApiHttpClient<BatchGetInvoicesResponse>();

  return useCallback(
    (batchGetInvoicesRequest: BatchGetInvoicesRequest, signal?: AbortSignal) => {
      return postInvoicesBatchGet({
        url: `/invoices/batch-get`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: batchGetInvoicesRequest,
        signal,
      });
    },
    [postInvoicesBatchGet],
  );
};

export const usePostInvoicesBatchGetMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>,
    TError,
    { data: BatchGetInvoicesRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>,
  TError,
  { data: BatchGetInvoicesRequest },
  TContext
> => {
  const mutationKey = ['postInvoicesBatchGet'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postInvoicesBatchGet = usePostInvoicesBatchGetHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>,
    { data: BatchGetInvoicesRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postInvoicesBatchGet(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostInvoicesBatchGetMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>
>;
export type PostInvoicesBatchGetMutationBody = BatchGetInvoicesRequest;
export type PostInvoicesBatchGetMutationError = ErrorResponse;

export const usePostInvoicesBatchGet = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>,
      TError,
      { data: BatchGetInvoicesRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostInvoicesBatchGetHook>>>,
  TError,
  { data: BatchGetInvoicesRequest },
  TContext
> => {
  return useMutation(usePostInvoicesBatchGetMutationOptions(options), queryClient);
};

/**
 * Creates a new payment flow.
 */
export const usePostPaymentFlowsHook = () => {
  const postPaymentFlows = usePaymentsApiHttpClient<PaymentFlowSuccessResponse>();

  return useCallback(
    (createPaymentFlowRequest: CreatePaymentFlowRequest, signal?: AbortSignal) => {
      return postPaymentFlows({
        url: `/payment-flows`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createPaymentFlowRequest,
        signal,
      });
    },
    [postPaymentFlows],
  );
};

export const usePostPaymentFlowsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>,
    TError,
    { data: CreatePaymentFlowRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>,
  TError,
  { data: CreatePaymentFlowRequest },
  TContext
> => {
  const mutationKey = ['postPaymentFlows'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postPaymentFlows = usePostPaymentFlowsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>,
    { data: CreatePaymentFlowRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postPaymentFlows(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPaymentFlowsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>
>;
export type PostPaymentFlowsMutationBody = CreatePaymentFlowRequest;
export type PostPaymentFlowsMutationError = ErrorResponse;

export const usePostPaymentFlows = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>,
      TError,
      { data: CreatePaymentFlowRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsHook>>>,
  TError,
  { data: CreatePaymentFlowRequest },
  TContext
> => {
  return useMutation(usePostPaymentFlowsMutationOptions(options), queryClient);
};

/**
 * Gets a payment flow by ID or external ID.
 */
export const useGetPaymentFlowsPaymentFlowRefHook = () => {
  const getPaymentFlowsPaymentFlowRef = usePaymentsApiHttpClient<PaymentFlowSuccessResponse>();

  return useCallback(
    (paymentFlowRef: string, signal?: AbortSignal) => {
      return getPaymentFlowsPaymentFlowRef({
        url: `/payment-flows/${paymentFlowRef}`,
        method: 'GET',
        signal,
      });
    },
    [getPaymentFlowsPaymentFlowRef],
  );
};

export const getGetPaymentFlowsPaymentFlowRefQueryKey = (paymentFlowRef: string) => {
  return [`/payment-flows/${paymentFlowRef}`] as const;
};

export const useGetPaymentFlowsPaymentFlowRefQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPaymentFlowsPaymentFlowRefQueryKey(paymentFlowRef);

  const getPaymentFlowsPaymentFlowRef = useGetPaymentFlowsPaymentFlowRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>> = ({
    signal,
  }) => getPaymentFlowsPaymentFlowRef(paymentFlowRef, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!paymentFlowRef,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPaymentFlowsPaymentFlowRefQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>
>;
export type GetPaymentFlowsPaymentFlowRefQueryError = ErrorResponse;

export function useGetPaymentFlowsPaymentFlowRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentFlowsPaymentFlowRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentFlowsPaymentFlowRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPaymentFlowsPaymentFlowRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPaymentFlowsPaymentFlowRefQueryOptions(paymentFlowRef, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetPaymentFlowsPaymentFlowRefSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPaymentFlowsPaymentFlowRefQueryKey(paymentFlowRef);

  const getPaymentFlowsPaymentFlowRef = useGetPaymentFlowsPaymentFlowRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>> = ({
    signal,
  }) => getPaymentFlowsPaymentFlowRef(paymentFlowRef, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPaymentFlowsPaymentFlowRefSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>
>;
export type GetPaymentFlowsPaymentFlowRefSuspenseQueryError = ErrorResponse;

export function useGetPaymentFlowsPaymentFlowRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentFlowsPaymentFlowRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentFlowsPaymentFlowRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPaymentFlowsPaymentFlowRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
  TError = ErrorResponse,
>(
  paymentFlowRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPaymentFlowsPaymentFlowRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPaymentFlowsPaymentFlowRefSuspenseQueryOptions(paymentFlowRef, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Searches payment flows.
 */
export const usePostPaymentFlowsSearchHook = () => {
  const postPaymentFlowsSearch = usePaymentsApiHttpClient<PaymentFlowListResponse>();

  return useCallback(
    (paymentFlowListRequest: PaymentFlowListRequest, signal?: AbortSignal) => {
      return postPaymentFlowsSearch({
        url: `/payment-flows/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: paymentFlowListRequest,
        signal,
      });
    },
    [postPaymentFlowsSearch],
  );
};

export const usePostPaymentFlowsSearchMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>,
    TError,
    { data: PaymentFlowListRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>,
  TError,
  { data: PaymentFlowListRequest },
  TContext
> => {
  const mutationKey = ['postPaymentFlowsSearch'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postPaymentFlowsSearch = usePostPaymentFlowsSearchHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>,
    { data: PaymentFlowListRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postPaymentFlowsSearch(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPaymentFlowsSearchMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>
>;
export type PostPaymentFlowsSearchMutationBody = PaymentFlowListRequest;
export type PostPaymentFlowsSearchMutationError = ErrorResponse;

export const usePostPaymentFlowsSearch = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>,
      TError,
      { data: PaymentFlowListRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentFlowsSearchHook>>>,
  TError,
  { data: PaymentFlowListRequest },
  TContext
> => {
  return useMutation(usePostPaymentFlowsSearchMutationOptions(options), queryClient);
};

/**
 * Gets a payment by ID or external ID.
 */
export const useGetPaymentsPaymentRefHook = () => {
  const getPaymentsPaymentRef = usePaymentsApiHttpClient<PaymentSuccessResponse>();

  return useCallback(
    (paymentRef: string, signal?: AbortSignal) => {
      return getPaymentsPaymentRef({
        url: `/payments/${paymentRef}`,
        method: 'GET',
        signal,
      });
    },
    [getPaymentsPaymentRef],
  );
};

export const getGetPaymentsPaymentRefQueryKey = (paymentRef: string) => {
  return [`/payments/${paymentRef}`] as const;
};

export const useGetPaymentsPaymentRefQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPaymentsPaymentRefQueryKey(paymentRef);

  const getPaymentsPaymentRef = useGetPaymentsPaymentRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>> = ({ signal }) =>
    getPaymentsPaymentRef(paymentRef, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!paymentRef,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPaymentsPaymentRefQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>
>;
export type GetPaymentsPaymentRefQueryError = ErrorResponse;

export function useGetPaymentsPaymentRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentsPaymentRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentsPaymentRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPaymentsPaymentRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPaymentsPaymentRefQueryOptions(paymentRef, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetPaymentsPaymentRefSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPaymentsPaymentRefQueryKey(paymentRef);

  const getPaymentsPaymentRef = useGetPaymentsPaymentRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>> = ({ signal }) =>
    getPaymentsPaymentRef(paymentRef, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPaymentsPaymentRefSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>
>;
export type GetPaymentsPaymentRefSuspenseQueryError = ErrorResponse;

export function useGetPaymentsPaymentRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentsPaymentRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetPaymentsPaymentRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetPaymentsPaymentRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>,
  TError = ErrorResponse,
>(
  paymentRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPaymentsPaymentRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetPaymentsPaymentRefSuspenseQueryOptions(paymentRef, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Searches payments.
 */
export const usePostPaymentsSearchHook = () => {
  const postPaymentsSearch = usePaymentsApiHttpClient<PaymentListResponse>();

  return useCallback(
    (paymentListRequest: PaymentListRequest, signal?: AbortSignal) => {
      return postPaymentsSearch({
        url: `/payments/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: paymentListRequest,
        signal,
      });
    },
    [postPaymentsSearch],
  );
};

export const usePostPaymentsSearchMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>,
    TError,
    { data: PaymentListRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>,
  TError,
  { data: PaymentListRequest },
  TContext
> => {
  const mutationKey = ['postPaymentsSearch'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postPaymentsSearch = usePostPaymentsSearchHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>,
    { data: PaymentListRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postPaymentsSearch(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostPaymentsSearchMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>
>;
export type PostPaymentsSearchMutationBody = PaymentListRequest;
export type PostPaymentsSearchMutationError = ErrorResponse;

export const usePostPaymentsSearch = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>,
      TError,
      { data: PaymentListRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostPaymentsSearchHook>>>,
  TError,
  { data: PaymentListRequest },
  TContext
> => {
  return useMutation(usePostPaymentsSearchMutationOptions(options), queryClient);
};

/**
 * Creates a product.
 */
export const usePostProductsHook = () => {
  const postProducts = usePaymentsApiHttpClient<ProductSuccessResponse>();

  return useCallback(
    (createProductRequest: CreateProductRequest, signal?: AbortSignal) => {
      return postProducts({
        url: `/products`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createProductRequest,
        signal,
      });
    },
    [postProducts],
  );
};

export const usePostProductsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
    TError,
    { data: CreateProductRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
  TError,
  { data: CreateProductRequest },
  TContext
> => {
  const mutationKey = ['postProducts'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postProducts = usePostProductsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
    { data: CreateProductRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postProducts(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostProductsMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>>;
export type PostProductsMutationBody = CreateProductRequest;
export type PostProductsMutationError = ErrorResponse;

export const usePostProducts = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
      TError,
      { data: CreateProductRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostProductsHook>>>,
  TError,
  { data: CreateProductRequest },
  TContext
> => {
  return useMutation(usePostProductsMutationOptions(options), queryClient);
};

/**
 * Lists all products.
 */
export const useGetProductsHook = () => {
  const getProducts = usePaymentsApiHttpClient<ProductListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getProducts({ url: `/products`, method: 'GET', signal });
    },
    [getProducts],
  );
};

export const getGetProductsQueryKey = () => {
  return [`/products`] as const;
};

export const useGetProductsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsQueryKey();

  const getProducts = useGetProductsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>> = ({ signal }) =>
    getProducts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>>;
export type GetProductsQueryError = ErrorResponse;

export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProducts<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetProductsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsQueryKey();

  const getProducts = useGetProductsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>> = ({ signal }) =>
    getProducts(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>>;
export type GetProductsSuspenseQueryError = ErrorResponse;

export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Retrieves a product by code.
 */
export const useGetProductsCodeHook = () => {
  const getProductsCode = usePaymentsApiHttpClient<ProductSuccessResponse>();

  return useCallback(
    (code: string, signal?: AbortSignal) => {
      return getProductsCode({
        url: `/products/${code}`,
        method: 'GET',
        signal,
      });
    },
    [getProductsCode],
  );
};

export const getGetProductsCodeQueryKey = (code: string) => {
  return [`/products/${code}`] as const;
};

export const useGetProductsCodeQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsCodeQueryKey(code);

  const getProductsCode = useGetProductsCodeHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>> = ({ signal }) =>
    getProductsCode(code, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!code,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProductsCodeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>>;
export type GetProductsCodeQueryError = ErrorResponse;

export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsCode<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsCodeQueryOptions(code, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetProductsCodeSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProductsCodeQueryKey(code);

  const getProductsCode = useGetProductsCodeHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>> = ({ signal }) =>
    getProductsCode(code, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProductsCodeSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>
>;
export type GetProductsCodeSuspenseQueryError = ErrorResponse;

export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetProductsCodeSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>,
  TError = ErrorResponse,
>(
  code: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProductsCodeHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetProductsCodeSuspenseQueryOptions(code, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Lists all transactions.
 */
export const useGetTransactionsHook = () => {
  const getTransactions = usePaymentsApiHttpClient<TransactionListResponse>();

  return useCallback(
    (params?: GetTransactionsParams, signal?: AbortSignal) => {
      return getTransactions({
        url: `/transactions`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getTransactions],
  );
};

export const getGetTransactionsQueryKey = (params?: GetTransactionsParams) => {
  return [`/transactions`, ...(params ? [params] : [])] as const;
};

export const useGetTransactionsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>>;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsQueryKey(params);

  const getTransactions = useGetTransactionsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>> = ({ signal }) =>
    getTransactions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>>;
export type GetTransactionsQueryError = ErrorResponse;

export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params: undefined | GetTransactionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactions<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetTransactionsSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsQueryKey(params);

  const getTransactions = useGetTransactionsHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>> = ({ signal }) =>
    getTransactions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionsSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>
>;
export type GetTransactionsSuspenseQueryError = ErrorResponse;

export function useGetTransactionsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params: undefined | GetTransactionsParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactionsSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>,
  TError = ErrorResponse,
>(
  params?: GetTransactionsParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a transaction.
 */
export const usePostTransactionsHook = () => {
  const postTransactions = usePaymentsApiHttpClient<TransactionSuccessResponse>();

  return useCallback(
    (syncTransactionRequest: SyncTransactionRequest, signal?: AbortSignal) => {
      return postTransactions({
        url: `/transactions`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: syncTransactionRequest,
        signal,
      });
    },
    [postTransactions],
  );
};

export const usePostTransactionsMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
    TError,
    { data: SyncTransactionRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
  TError,
  { data: SyncTransactionRequest },
  TContext
> => {
  const mutationKey = ['postTransactions'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postTransactions = usePostTransactionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
    { data: SyncTransactionRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postTransactions(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTransactionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>
>;
export type PostTransactionsMutationBody = SyncTransactionRequest;
export type PostTransactionsMutationError = ErrorResponse;

export const usePostTransactions = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
      TError,
      { data: SyncTransactionRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsHook>>>,
  TError,
  { data: SyncTransactionRequest },
  TContext
> => {
  return useMutation(usePostTransactionsMutationOptions(options), queryClient);
};

/**
 * Retrieves a transaction by ID or external ID.
 */
export const useGetTransactionsTransactionRefHook = () => {
  const getTransactionsTransactionRef = usePaymentsApiHttpClient<TransactionSuccessResponse>();

  return useCallback(
    (transactionRef: string, signal?: AbortSignal) => {
      return getTransactionsTransactionRef({
        url: `/transactions/${transactionRef}`,
        method: 'GET',
        signal,
      });
    },
    [getTransactionsTransactionRef],
  );
};

export const getGetTransactionsTransactionRefQueryKey = (transactionRef: string) => {
  return [`/transactions/${transactionRef}`] as const;
};

export const useGetTransactionsTransactionRefQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsTransactionRefQueryKey(transactionRef);

  const getTransactionsTransactionRef = useGetTransactionsTransactionRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>> = ({
    signal,
  }) => getTransactionsTransactionRef(transactionRef, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!transactionRef,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetTransactionsTransactionRefQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>
>;
export type GetTransactionsTransactionRefQueryError = ErrorResponse;

export function useGetTransactionsTransactionRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactionsTransactionRef<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsTransactionRefQueryOptions(transactionRef, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetTransactionsTransactionRefSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsTransactionRefQueryKey(transactionRef);

  const getTransactionsTransactionRef = useGetTransactionsTransactionRefHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>> = ({
    signal,
  }) => getTransactionsTransactionRef(transactionRef, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionsTransactionRefSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>
>;
export type GetTransactionsTransactionRefSuspenseQueryError = ErrorResponse;

export function useGetTransactionsTransactionRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactionsTransactionRefSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsTransactionRefSuspenseQueryOptions(transactionRef, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates a transaction.
 */
export const usePatchTransactionsTransactionRefHook = () => {
  const patchTransactionsTransactionRef = usePaymentsApiHttpClient<TransactionSuccessResponse>();

  return useCallback(
    (transactionRef: string, updateTransactionRequest: UpdateTransactionRequest, signal?: AbortSignal) => {
      return patchTransactionsTransactionRef({
        url: `/transactions/${transactionRef}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateTransactionRequest,
        signal,
      });
    },
    [patchTransactionsTransactionRef],
  );
};

export const usePatchTransactionsTransactionRefMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>,
    TError,
    { transactionRef: string; data: UpdateTransactionRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>,
  TError,
  { transactionRef: string; data: UpdateTransactionRequest },
  TContext
> => {
  const mutationKey = ['patchTransactionsTransactionRef'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const patchTransactionsTransactionRef = usePatchTransactionsTransactionRefHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>,
    { transactionRef: string; data: UpdateTransactionRequest }
  > = (props) => {
    const { transactionRef, data } = props ?? {};

    return patchTransactionsTransactionRef(transactionRef, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchTransactionsTransactionRefMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>
>;
export type PatchTransactionsTransactionRefMutationBody = UpdateTransactionRequest;
export type PatchTransactionsTransactionRefMutationError = ErrorResponse;

export const usePatchTransactionsTransactionRef = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>,
      TError,
      { transactionRef: string; data: UpdateTransactionRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePatchTransactionsTransactionRefHook>>>,
  TError,
  { transactionRef: string; data: UpdateTransactionRequest },
  TContext
> => {
  return useMutation(usePatchTransactionsTransactionRefMutationOptions(options), queryClient);
};

/**
 * Retrieves the version history of a transaction.
 */
export const useGetTransactionsTransactionRefHistoryHook = () => {
  const getTransactionsTransactionRefHistory = usePaymentsApiHttpClient<TransactionHistoryResponse>();

  return useCallback(
    (transactionRef: string, signal?: AbortSignal) => {
      return getTransactionsTransactionRefHistory({
        url: `/transactions/${transactionRef}/history`,
        method: 'GET',
        signal,
      });
    },
    [getTransactionsTransactionRefHistory],
  );
};

export const getGetTransactionsTransactionRefHistoryQueryKey = (transactionRef: string) => {
  return [`/transactions/${transactionRef}/history`] as const;
};

export const useGetTransactionsTransactionRefHistoryQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsTransactionRefHistoryQueryKey(transactionRef);

  const getTransactionsTransactionRefHistory = useGetTransactionsTransactionRefHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>> = ({
    signal,
  }) => getTransactionsTransactionRefHistory(transactionRef, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!transactionRef,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionsTransactionRefHistoryQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>
>;
export type GetTransactionsTransactionRefHistoryQueryError = ErrorResponse;

export function useGetTransactionsTransactionRefHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactionsTransactionRefHistory<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsTransactionRefHistoryQueryOptions(transactionRef, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetTransactionsTransactionRefHistorySuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTransactionsTransactionRefHistoryQueryKey(transactionRef);

  const getTransactionsTransactionRefHistory = useGetTransactionsTransactionRefHistoryHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>> = ({
    signal,
  }) => getTransactionsTransactionRefHistory(transactionRef, signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTransactionsTransactionRefHistorySuspenseQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>
>;
export type GetTransactionsTransactionRefHistorySuspenseQueryError = ErrorResponse;

export function useGetTransactionsTransactionRefHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetTransactionsTransactionRefHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetTransactionsTransactionRefHistorySuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
  TError = ErrorResponse,
>(
  transactionRef: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTransactionsTransactionRefHistoryHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetTransactionsTransactionRefHistorySuspenseQueryOptions(transactionRef, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Searches transaction allocations.
 */
export const usePostTransactionsAllocationsSearchHook = () => {
  const postTransactionsAllocationsSearch = usePaymentsApiHttpClient<SearchAllocationsResponse>();

  return useCallback(
    (searchAllocationsRequest: SearchAllocationsRequest, signal?: AbortSignal) => {
      return postTransactionsAllocationsSearch({
        url: `/transactions/allocations/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchAllocationsRequest,
        signal,
      });
    },
    [postTransactionsAllocationsSearch],
  );
};

export const usePostTransactionsAllocationsSearchMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>,
    TError,
    { data: SearchAllocationsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>,
  TError,
  { data: SearchAllocationsRequest },
  TContext
> => {
  const mutationKey = ['postTransactionsAllocationsSearch'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postTransactionsAllocationsSearch = usePostTransactionsAllocationsSearchHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>,
    { data: SearchAllocationsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postTransactionsAllocationsSearch(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTransactionsAllocationsSearchMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>
>;
export type PostTransactionsAllocationsSearchMutationBody = SearchAllocationsRequest;
export type PostTransactionsAllocationsSearchMutationError = ErrorResponse;

export const usePostTransactionsAllocationsSearch = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>,
      TError,
      { data: SearchAllocationsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsAllocationsSearchHook>>>,
  TError,
  { data: SearchAllocationsRequest },
  TContext
> => {
  return useMutation(usePostTransactionsAllocationsSearchMutationOptions(options), queryClient);
};

/**
 * Searches transactions.
 */
export const usePostTransactionsSearchHook = () => {
  const postTransactionsSearch = usePaymentsApiHttpClient<SearchTransactionsResponse>();

  return useCallback(
    (searchTransactionsRequest: SearchTransactionsRequest, signal?: AbortSignal) => {
      return postTransactionsSearch({
        url: `/transactions/search`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: searchTransactionsRequest,
        signal,
      });
    },
    [postTransactionsSearch],
  );
};

export const usePostTransactionsSearchMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>,
    TError,
    { data: SearchTransactionsRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>,
  TError,
  { data: SearchTransactionsRequest },
  TContext
> => {
  const mutationKey = ['postTransactionsSearch'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postTransactionsSearch = usePostTransactionsSearchHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>,
    { data: SearchTransactionsRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postTransactionsSearch(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTransactionsSearchMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>
>;
export type PostTransactionsSearchMutationBody = SearchTransactionsRequest;
export type PostTransactionsSearchMutationError = ErrorResponse;

export const usePostTransactionsSearch = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>,
      TError,
      { data: SearchTransactionsRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostTransactionsSearchHook>>>,
  TError,
  { data: SearchTransactionsRequest },
  TContext
> => {
  return useMutation(usePostTransactionsSearchMutationOptions(options), queryClient);
};

/**
 * Creates a user.
 */
export const usePostUsersHook = () => {
  const postUsers = usePaymentsApiHttpClient<UserSuccessResponse>();

  return useCallback(
    (createUserRequest: CreateUserRequest, signal?: AbortSignal) => {
      return postUsers({
        url: `/users`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createUserRequest,
        signal,
      });
    },
    [postUsers],
  );
};

export const usePostUsersMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
    TError,
    { data: CreateUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  const mutationKey = ['postUsers'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const postUsers = usePostUsersHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
    { data: CreateUserRequest }
  > = (props) => {
    const { data } = props ?? {};

    return postUsers(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostUsersMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>>;
export type PostUsersMutationBody = CreateUserRequest;
export type PostUsersMutationError = ErrorResponse;

export const usePostUsers = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
      TError,
      { data: CreateUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePostUsersHook>>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  return useMutation(usePostUsersMutationOptions(options), queryClient);
};

/**
 * Lists all users.
 */
export const useGetUsersHook = () => {
  const getUsers = usePaymentsApiHttpClient<UserListResponse>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getUsers({ url: `/users`, method: 'GET', signal });
    },
    [getUsers],
  );
};

export const getGetUsersQueryKey = () => {
  return [`/users`] as const;
};

export const useGetUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey();

  const getUsers = useGetUsersHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>> = ({ signal }) =>
    getUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>>;
export type GetUsersQueryError = ErrorResponse;

export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetUsers<TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError = ErrorResponse>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetUsersQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

export const useGetUsersSuspenseQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(options?: {
  query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUsersQueryKey();

  const getUsers = useGetUsersHook();

  const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>> = ({ signal }) =>
    getUsers(signal);

  return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUsersSuspenseQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>>;
export type GetUsersSuspenseQueryError = ErrorResponse;

export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options: {
    query: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetUsersSuspense<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>,
  TError = ErrorResponse,
>(
  options?: {
    query?: Partial<UseSuspenseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetUsersHook>>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseSuspenseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = useGetUsersSuspenseQueryOptions(options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates a user.
 */
export const usePatchUsersUserRefHook = () => {
  const patchUsersUserRef = usePaymentsApiHttpClient<UserSuccessResponse>();

  return useCallback(
    (userRef: string, updateUserRequest: UpdateUserRequest, signal?: AbortSignal) => {
      return patchUsersUserRef({
        url: `/users/${userRef}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateUserRequest,
        signal,
      });
    },
    [patchUsersUserRef],
  );
};

export const usePatchUsersUserRefMutationOptions = <TError = ErrorResponse, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>,
    TError,
    { userRef: string; data: UpdateUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>,
  TError,
  { userRef: string; data: UpdateUserRequest },
  TContext
> => {
  const mutationKey = ['patchUsersUserRef'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const patchUsersUserRef = usePatchUsersUserRefHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>,
    { userRef: string; data: UpdateUserRequest }
  > = (props) => {
    const { userRef, data } = props ?? {};

    return patchUsersUserRef(userRef, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchUsersUserRefMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>
>;
export type PatchUsersUserRefMutationBody = UpdateUserRequest;
export type PatchUsersUserRefMutationError = ErrorResponse;

export const usePatchUsersUserRef = <TError = ErrorResponse, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>,
      TError,
      { userRef: string; data: UpdateUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePatchUsersUserRefHook>>>,
  TError,
  { userRef: string; data: UpdateUserRequest },
  TContext
> => {
  return useMutation(usePatchUsersUserRefMutationOptions(options), queryClient);
};
